import { useParams, useLocation } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { userApi, trainingApi } from '../../api'
import { trainingProgramActivationApi } from '../../api/trainingProgramActivationApi'
import type { UserRelation, TrainingSet, TrainingProgramActivation } from '../../types/api'
import { UserDetail } from './UserDetail'

interface LocationState {
  coach?: UserRelation
}

export default function CoachDetail() {
  const { id: coachId } = useParams<{ id: string }>()
  const location = useLocation()
  const state = location.state as LocationState

  const [coach, setCoach] = useState<UserRelation | null>(state?.coach || null)
  const [trainingSets, setTrainingSets] = useState<TrainingSet[]>([])
  const [traineeAssignment, setTraineeAssignment] = useState<TrainingProgramActivation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadCoachData()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coachId, coach?.id])

  async function loadCoachData() {
    if (!coachId) {
      setError('No coach ID provided')
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    
    try {
      let coachData = coach || state?.coach
      if (!coachData) {
        const coaches = await userApi.getCoaches()
        const foundCoach = coaches.find(c => c.id === parseInt(coachId))

        if (!foundCoach) {
          setError('Coach not found')
          setLoading(false)
          return
        }
        setCoach(foundCoach)
        coachData = foundCoach
      }

      const [sets, activations] = await Promise.all([
        trainingApi.getCoachSets(coachData.id),
        trainingProgramActivationApi.getHistory().catch(() => [] as TrainingProgramActivation[]),
      ])
      setTrainingSets(sets)
      setTraineeAssignment(
        activations.find(a => a.isActive && a.assignedByCoachId === coachData.id) ?? null,
      )
    } catch (err) {
      console.error('Error loading coach:', err)
      setError(err instanceof Error ? err.message : 'Failed to load coach details')
    } finally {
      setLoading(false)
    }
  }

  if (!coach && !loading && !error) {
    return null
  }

  return (
    <UserDetail
      user={coach!}
      trainingSets={trainingSets}
      traineeAssignment={traineeAssignment}
      loading={loading}
      error={error}
      userType="coach"
      backPath="/my-coaches"
      userId={coachId || ''}
      onRetry={loadCoachData}
    />
  )
}
